import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { SpanHeadingS } from '@eneco/custom-components/CustomerReview';
import { useCustomerPublicGetCustomerRatings } from '@eneco/dc/hooks';
import { Bleed, Box, Card, Grid, Heading, Hidden, RatingStar, Stack, Stretch, Text, TextLink } from '@eneco/sparky';
import type { HeadingProps } from '@eneco/sparky/types';

import type { CustomerReviewContent } from './customer-review-content.types';

/**
 * `CustomerReview` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component's left-hand summary column (`apps/customerreview/shared/multilabel/src/
 * CustomerReview.tsx`, registered under the `CustomerReview` componentName — see
 * `containers/*\/sitecoreComponents.ts`): title, content, the rating (live from the Digital Core when
 * `getLiveRatingDataOption` is set, otherwise the authored `ratingNumber`), and a link.
 *
 * Known simplification: the review cards themselves (`items`) have no resolvable content on
 * `?_cms=sanity` yet — see `customer-review-content.types.ts`. They render normally on `?_cms=sitecore`.
 *
 * Known simplification: the legacy component formats the rating and interpolates `ratingSubText`'s
 * `{numberOfReviews}` placeholder via `@eneco/i18n`'s `useFormatter`, which lives in `lib:i18n` —
 * unreachable from this lane's module boundary (`nx.json`). Replicated here with a plain one-decimal
 * format and a literal `{numberOfReviews}` substring replace, which covers this field's actual usage but
 * not arbitrary ICU plural/select syntax.
 */
const CustomerReview: FC = () => {
  const { adapter } = useCms();
  const content = useContent<CustomerReviewContent>();
  const headingLevel = (useSetting('headingLevel') || 'h2') as HeadingProps['as'];

  const { data } = useCustomerPublicGetCustomerRatings(content.getLiveRatingDataOption === true ? undefined : null);

  const ratingNumber = content.getLiveRatingDataOption ? data?.averageScore : Number(content.ratingNumber);
  const starRating = ratingNumber && Math.round(ratingNumber / 2);

  const html = adapter.toRichText(content.content);
  const link = linkWithUrl(content.link);

  const ratingSubText = content.ratingSubText?.replace('{numberOfReviews}', String(data?.ratingsAmount ?? ''));

  return (
    <Grid columns={{ md: '2', lg: '3' }} gap="16">
      <Grid.Item>
        <Stack gap="6" alignY={{ sm: 'justify' }}>
          {content.title && (
            <Heading as={headingLevel} size={{ initial: 'S', md: 'M' }} color="inherit">
              {content.title}
            </Heading>
          )}
          {html && (
            <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
              <RichTextComponent html={html} />
            </Text>
          )}
          <Stack.Item>
            <SpanHeadingS>
              {!!ratingNumber && !!starRating && (
                <Stack direction="row" alignY="end" gap="2">
                  <Text>{ratingNumber.toFixed(1).replace('.', ',')} </Text>
                  <RatingStar maxStars={5} score={ratingNumber} size="large" />
                </Stack>
              )}
            </SpanHeadingS>
            {ratingSubText && link?.href && <Text size={{ initial: 'BodyS' }}>{ratingSubText} </Text>}
          </Stack.Item>

          {link?.text && (
            <Box>
              <TextLink href={link.href} target={link.target} emphasis="high">
                {link.text}
              </TextLink>
            </Box>
          )}
        </Stack>
      </Grid.Item>
      {(content.items ?? []).map((item, index) => {
        const maxStars = 5;
        const itemHtml = adapter.toRichText(item.content);
        const itemRatingNumber = Number(item.ratingNumber);

        const review = (
          <Bleed top="8" key={item.id ?? String(index)}>
            <Stretch>
              <Grid.Item>
                <Stretch>
                  <Grid gridTemplateColumns="auto auto 24px" gridTemplateRows="32px auto">
                    <Grid.Item gridColumn="1/-1" gridRow="2">
                      <Stretch>
                        <Card>
                          <Box padding={{ initial: '5', md: '10' }} paddingTop="10">
                            <Stack gap="6">
                              {itemHtml && (
                                <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                                  <RichTextComponent html={itemHtml} />
                                </Text>
                              )}
                              <Stack.Item>
                                {item.title && (
                                  <Text size={{ initial: 'BodyS', md: 'BodyM' }} color="textLowEmphasis">
                                    {item.title}
                                  </Text>
                                )}
                                {item.ratingNumber && (
                                  <Stack direction="row" alignY="start" gap="2">
                                    <Text weight="bold" size="BodyL">
                                      {Math.round(itemRatingNumber)}/{maxStars * 2}
                                    </Text>
                                    <RatingStar maxStars={maxStars} score={itemRatingNumber} size="medium" />
                                  </Stack>
                                )}
                              </Stack.Item>
                            </Stack>
                          </Box>
                        </Card>
                      </Stretch>
                    </Grid.Item>
                  </Grid>
                </Stretch>
              </Grid.Item>
            </Stretch>
          </Bleed>
        );

        return index === 1 ? (
          <Hidden below="lg" key={item.id ?? String(index)}>
            {review}
          </Hidden>
        ) : (
          review
        );
      })}
    </Grid>
  );
};

export default CustomerReview;
